import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useLevel } from '../context/LevelContext';
import { ticketAPI } from '../api/client';
import { buildLevelPath } from '../utils/routeLanguage';
import { ArrowLeft, Upload } from 'lucide-react';

export const TicketCreatePage = () => {
  const { t, language } = useLanguage();
  const { selectedLevel } = useLevel();
  const navigate = useNavigate();
  const levelPath = buildLevelPath(language, selectedLevel);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFileUpload = (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) { setError(t('onlyPngOrJpeg')); return; }
    if (file.size > 5 * 1024 * 1024) { setError(t('fileTooLarge')); return; }
    const reader = new FileReader();
    reader.onload = (e) => setScreenshot(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!subject.trim() || !description.trim()) { setError(t('ticketRequiredFields')); return; }
    try {
      setLoading(true);
      await ticketAPI.createTicket({ subject: subject.trim(), description: description.trim(), screenshot });
      setSuccess(t('ticketCreatedSuccess'));
      setSubject('');
      setDescription('');
      setScreenshot(null);
    } catch (err) {
      setError(err.response?.data?.error || t('ticketCreateError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ticket-page">
      <div className="ticket-container">
        <button onClick={() => navigate(levelPath)} className="ticket-back-btn">
          <ArrowLeft size={16} />
          {t('back')}
        </button>

        <div className="ticket-card">
          <h1 className="ticket-page-title">{t('createTicket')}</h1>
          <p className="ticket-page-desc">{t('ticketHelpText')}</p>

          {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
          {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">{t('ticketSubject')}</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="form-input"
                placeholder={t('ticketSubjectPlaceholder')}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('ticketDescription')}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                className="form-textarea"
                placeholder={t('ticketDescriptionPlaceholder')}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('ticketScreenshot')}</label>
              <div className="ticket-upload-area">
                {screenshot ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <img src={screenshot} alt="Ticket screenshot" style={{ width: '100%', maxHeight: 256, objectFit: 'contain', borderRadius: 8 }} />
                    <button type="button" onClick={() => setScreenshot(null)} className="ticket-remove-btn">
                      {t('removeScreenshot')}
                    </button>
                  </div>
                ) : (
                  <label className="ticket-upload-label">
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={(e) => handleFileUpload(e.target.files?.[0])}
                      style={{ display: 'none' }}
                    />
                    <Upload size={20} style={{ color: 'rgba(245,244,240,0.3)' }} />
                    <span className="ticket-upload-text">{t('attachScreenshot')}</span>
                    <span className="ticket-upload-hint">{t('ticketScreenshotHint')}</span>
                  </label>
                )}
              </div>
            </div>

            <button type="submit" disabled={loading} className="modern-button-primary" style={{ width: '100%' }}>
              {loading ? t('loading') : t('submitTicket')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
